/**
 * A linha do tempo da entrega.
 *
 * Desenha-se por ordem: o anel do primeiro passo fecha, o fio enche até ao
 * seguinte, esse acende, e assim até ao fim. No fim espera, apaga-se e recomeça.
 *
 * Os fios são medidos, não escritos: a posição deles depende de onde os
 * símbolos ficam, e isso muda com a largura da coluna. Em percentagens
 * escorregavam a cada tamanho de ecrã.
 */
const VOLTA = 150.796; // o perímetro do anel, em unidades do desenho
const ANEL = 900;
const FIO = 1100;
const PAUSA = 5000;
const APAGA = 800;

/** Dias úteis: os CTT não entregam ao sábado nem ao domingo */
function uteis(dia, quantos) {
  const r = new Date(dia);
  let feitos = 0;
  while (feitos < quantos) {
    r.setDate(r.getDate() + 1);
    const semana = r.getDay();
    if (semana !== 0 && semana !== 6) feitos += 1;
  }
  return r;
}

function escreve(dia) {
  return dia.toLocaleDateString('pt-PT', { weekday: 'short', day: 'numeric', month: 'numeric' });
}

/** Uma passagem suave de `de` até `a`, com travão nas duas pontas */
function anima(de, a, duracao, aCada, noFim) {
  const inicio = performance.now();
  const suave = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

  let pedido = 0;
  const passo = (agora) => {
    const p = Math.min((agora - inicio) / duracao, 1);
    aCada(de + (a - de) * suave(p));
    if (p < 1) pedido = requestAnimationFrame(passo);
    else if (noFim) noFim();
  };

  pedido = requestAnimationFrame(passo);
  return () => cancelAnimationFrame(pedido);
}

class ClaroEntrega extends HTMLElement {
  #relogios = [];
  #parar = null;

  connectedCallback() {
    this.aneis = [...this.querySelectorAll('[data-anel]')];
    this.dentros = [...this.querySelectorAll('[data-dentro]')];
    this.datas = [...this.querySelectorAll('[data-data]')];
    this.passos = [...this.querySelectorAll('[data-passo]')];
    this.fios = [...this.querySelectorAll('[data-fio]')];
    this.bases = [...this.querySelectorAll('[data-fio-base]')];

    this.#escreveDatas();

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#tudoFeito();
      return;
    }

    this.#mede();
    addEventListener('resize', () => this.#mede(), { passive: true });

    // Só arranca quando entra no ecrã: a correr desde o topo da página, já
    // tinha dado a volta antes de alguém a ver.
    new IntersectionObserver(
      ([entrada], vigia) => {
        if (!entrada.isIntersecting) return;
        vigia.disconnect();
        this.#corre();
      },
      { threshold: 0.4 }
    ).observe(this);
  }

  disconnectedCallback() {
    this.#limpa();
  }

  /* ── datas ── */
  #escreveDatas() {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    [hoje, uteis(hoje, 1), uteis(hoje, 2)].forEach((dia, i) => {
      if (this.datas[i]) this.datas[i].textContent = escreve(dia);
    });
  }

  /* ── onde ficam os fios ────────────────────────────────────
     Medido a partir do centro de cada símbolo, com uma folga para o fio não
     encostar ao anel. */
  #mede() {
    const caixa = this.querySelector('.claro-linha-tempo__passos');
    if (!caixa || this.passos.length < 3) return;

    const fora = caixa.getBoundingClientRect();
    const meios = this.passos.map((p) => {
      const r = p.getBoundingClientRect();
      return { x: r.left + r.width / 2 - fora.left, y: r.top + r.height / 2 - fora.top, raio: r.width / 2 };
    });

    this.trocos = [0, 1].map((i) => {
      const folga = meios[i].raio + 3;
      const x1 = meios[i].x + folga;
      const x2 = meios[i + 1].x - folga;
      return { x1, y: meios[i].y, largura: Math.max(0, x2 - x1) };
    });

    this.trocos.forEach((t, i) => {
      this.#poe(this.bases[i], t.x1, t.x1 + t.largura, t.y);
      this.#poe(this.fios[i], t.x1, t.x1, t.y);
    });
  }

  #poe(linha, x1, x2, y) {
    if (!linha) return;
    linha.setAttribute('x1', x1.toFixed(1));
    linha.setAttribute('x2', x2.toFixed(1));
    linha.setAttribute('y1', y.toFixed(1));
    linha.setAttribute('y2', y.toFixed(1));
  }

  /* ── as peças ── */
  #anel(i, quanto) {
    const feito = VOLTA * Math.max(0, Math.min(1, quanto));
    this.aneis[i]?.setAttribute('stroke-dasharray', `${feito.toFixed(1)} ${(VOLTA - feito).toFixed(1)}`);
  }

  #fio(i, quanto) {
    const t = this.trocos?.[i];
    if (!t || !this.fios[i]) return;
    this.fios[i].setAttribute('x2', (t.x1 + t.largura * Math.max(0, Math.min(1, quanto))).toFixed(1));
  }

  #estado(i, qual) {
    const dentro = this.dentros[i];
    if (!dentro) return;
    if (qual) dentro.dataset.estado = qual;
    else delete dentro.dataset.estado;
  }

  /* ── a sequência ── */
  #espera(fn, ms) {
    this.#relogios.push(setTimeout(fn, ms));
  }

  #limpa() {
    this.#parar?.();
    this.#relogios.forEach(clearTimeout);
    this.#relogios = [];
  }

  #zera() {
    [0, 1, 2].forEach((i) => {
      this.#anel(i, 0);
      this.#estado(i, null);
    });
    [0, 1].forEach((i) => this.#fio(i, 0));
  }

  #tudoFeito() {
    [0, 1, 2].forEach((i) => {
      this.#anel(i, 1);
      this.#estado(i, 'feito');
    });
    [0, 1].forEach((i) => this.#fio(i, 1));
  }

  #corre() {
    this.#limpa();
    this.#zera();

    // Cada passo: acende, fecha o anel, e puxa o fio até ao seguinte
    const passo = (i) => {
      this.#estado(i, i === 0 ? 'feito' : 'agora');

      this.#parar = anima(0, 1, ANEL, (p) => this.#anel(i, p), () => {
        this.#estado(i, 'feito');

        if (i === this.aneis.length - 1) {
          this.#espera(() => this.#recomeca(), PAUSA);
          return;
        }

        this.#parar = anima(0, 1, FIO, (p) => this.#fio(i, p), () => passo(i + 1));
      });
    };

    this.#espera(() => passo(0), 300);
  }

  #recomeca() {
    this.#parar = anima(1, 0, APAGA, (p) => {
      [0, 1, 2].forEach((i) => this.#anel(i, p));
      [0, 1].forEach((i) => this.#fio(i, p));
    }, () => {
      this.#zera();
      this.#espera(() => this.#corre(), 350);
    });
  }
}

if (!customElements.get('claro-entrega')) customElements.define('claro-entrega', ClaroEntrega);
